import React, { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box, Card, CardContent, Typography, Button, Chip, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import {
  api, STORAGE_BASE_URL,
  type Inspection, type Defect, type DetectionResult, type InspectionImage,
} from '../services/api';
import DefectOverlay from '../components/DefectOverlay';
import LatestCaptureCard from '../components/LatestCaptureCard';
import { getSeverityStyles } from '../components/ui/severity';

export default function ReportPage() {
  const theme = useTheme();
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [defects, setDefects] = useState<Defect[]>([]);
  const [images, setImages] = useState<InspectionImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [imageDialogUrl, setImageDialogUrl] = useState<string | null>(null);
  const [imageDialogTitle, setImageDialogTitle] = useState<string>('Annotated image');
  const [droppedImage, setDroppedImage] = useState<string | null>(null);
  const [lastDetections, setLastDetections] = useState<DetectionResult[]>([]);

  useEffect(() => {
    if (!inspectionId) return;
    Promise.all([
      api.getInspection(inspectionId),
      api.getDefects(inspectionId),
      // Drives the per-capture rows so frames with no findings stay visible.
      api.getInspectionImages(inspectionId).catch(() => [] as InspectionImage[]),
    ])
      .then(([insp, defs, imgs]) => { setInspection(insp); setDefects(defs); setImages(imgs); })
      .finally(() => setLoading(false));
  }, [inspectionId]);

  const getDefectImageKey = (defect: Defect): string | null => defect.annotated_image_key ?? null;

  /**
   * Resolves the image URL at click time rather than reusing one embedded in the
   * page's initial load. Those carry a fixed expiry and are signed with the ECS
   * task's temporary credentials, so they break once the expiry passes or the
   * task is replaced — which showed up as images that only loaded after a refresh.
   */
  const openImageByKey = async (key: string | null, title: string) => {
    if (!key || !inspectionId) {
      toast.error('No image available for this capture');
      return;
    }
    try {
      const url = await api.getImageUrl(inspectionId, key);
      setImageDialogTitle(title);
      setImageDialogUrl(url);
    } catch {
      toast.error('Could not load the image');
    }
  };

  const openImageDialog = (defect: Defect, title?: string) =>
    openImageByKey(getDefectImageKey(defect), title ?? `${defect.defect_type.toUpperCase()} image`);

  const closeImageDialog = () => {
    setImageDialogUrl(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/*': [] }, multiple: false,
    onDrop: async ([file]) => {
      if (!inspectionId) return;

      // Show the dropped image inline for the overlay
      const reader = new FileReader();
      reader.onload = () => setDroppedImage(reader.result as string);
      reader.readAsDataURL(file);

      setAnalysing(true);
      setLastDetections([]);
      try {
        const result = await api.analyzeImage(inspectionId, file);
        setLastDetections(result.detections || []);
        setDefects(prev => [...prev, ...result.detections.map((d: any, i: number) => ({
          id: `new_${i}`, inspection_id: inspectionId, defect_type: d.defect_type,
          severity: d.severity, confidence: d.confidence, description: d.description,
          is_confirmed: false, created_at: new Date().toISOString(),
          annotated_image_url: result.annotated_image_url ?? undefined,
        }))]);
        // A clean frame is a valid result, not a failure — say so plainly.
        toast.success(
          result.defects_created === 0
            ? 'No defects detected'
            : `${result.defects_created} defect(s) detected`,
        );
        // Re-fetch captures too, so a zero-defect upload shows up right away.
        api.getInspectionImages(inspectionId).then(setImages).catch(() => {});
        // Refresh real defects
        api.getDefects(inspectionId).then(setDefects);
      } catch { toast.error('Analysis failed'); }
      finally { setAnalysing(false); }
    }
  });

  const confirmDefect = async (defect: Defect, confirmed: boolean) => {
    try {
      const updated = await api.confirmDefect(defect.id, confirmed);
      setDefects(prev => prev.map(d => d.id === updated.id ? updated : d));
      toast.success(confirmed ? 'Defect confirmed' : 'Defect rejected');
    } catch { toast.error('Could not update defect'); }
  };

  const downloadReport = async (format: 'pdf' | 'csv') => {
    if (!inspectionId) return;
    setGenerating(true);
    const popup = window.open('', '_blank');
    if (!popup) {
      toast.error('Please allow popups to download the report');
      setGenerating(false);
      return;
    }

    try {
      let url = await api.generateReport(inspectionId, format);
      if (url.startsWith('/local-storage/')) {
        url = `${STORAGE_BASE_URL}${url}`;
      }
      popup.location.href = url;
      toast.success(`${format.toUpperCase()} report generated`);
    } catch {
      popup.close();
      toast.error('Report generation failed');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>;

  const stats = {
    total: defects.length,
    confirmed: defects.filter(d => d.is_confirmed).length,
    critical: defects.filter(d => d.severity === 'critical').length,
    high: defects.filter(d => d.severity === 'high').length,
  };

  /**
   * One group per analyzed capture, driven by the images list rather than
   * inferred from defects — that's what keeps a clean frame visible, since it
   * owns no defects to infer it from.
   *
   * Images arrive oldest-first so #1 is the oldest; display order is newest
   * first. Defects with no image_id, or pointing at an image missing from the
   * list, fall into a trailing bucket rather than disappearing.
   */
  const grouped = (() => {
    const byImage = new Map<string, Defect[]>();
    const ungrouped: Defect[] = [];
    for (const d of defects) {
      if (d.image_id) {
        const arr = byImage.get(d.image_id) ?? [];
        arr.push(d);
        byImage.set(d.image_id, arr);
      } else {
        ungrouped.push(d);
      }
    }

    const groups = images.map((img, i) => ({
      imageId: img.id,
      index: i + 1,
      image: img,
      defects: byImage.get(img.id) ?? [],
      timestamp: img.captured_at,
    }));

    const known = new Set(images.map(img => img.id));
    for (const [imageId, defs] of byImage) {
      if (!known.has(imageId)) ungrouped.push(...defs);
    }

    groups.reverse();
    return { groups, ungrouped };
  })();

  const renderDefectRow = (defect: Defect) => (
    <TableRow key={defect.id} sx={{ '&:hover': { bgcolor: 'background.default' } }}>
      <TableCell sx={{ color: 'text.primary', borderColor: 'divider', fontWeight: 600, fontSize: 13 }}>
        {defect.defect_type.toUpperCase()}
      </TableCell>
      <TableCell sx={{ borderColor: 'divider' }}>
        <Chip label={defect.severity} size="small" sx={{ ...getSeverityStyles(theme, defect.severity), fontWeight: 700 }} />
      </TableCell>
      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 13 }}>
        {defect.confidence != null ? `${Math.round(defect.confidence * 100)}%` : '–'}
      </TableCell>
      <TableCell sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 12 }}>
        {(defect.description || '').slice(0, 60)}
      </TableCell>
      <TableCell sx={{ borderColor: 'divider' }}>
        <Chip
          label={defect.is_confirmed ? 'Confirmed' : 'Pending'}
          size="small"
          sx={{ bgcolor: defect.is_confirmed ? 'success.dark' : alpha(theme.palette.text.primary, 0.02), color: defect.is_confirmed ? 'success.main' : 'text.secondary' }} />
      </TableCell>
      <TableCell sx={{ borderColor: 'divider' }}>
        {getDefectImageKey(defect) && (
          <Tooltip title="View annotated image">
            <IconButton size="small" onClick={() => openImageDialog(defect)} sx={{ color: 'primary.main' }}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {!defect.is_confirmed && (
          <Tooltip title="Confirm">
            <IconButton size="small" onClick={() => confirmDefect(defect, true)} sx={{ color: 'success.main' }}>
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Reject / Delete">
          <IconButton size="small" onClick={() => { api.deleteDefect(defect.id).then(() => setDefects(prev => prev.filter(d => d.id !== defect.id))); }} sx={{ color: 'error.main' }}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{inspection?.title}</Typography>
          {inspection?.location_description && <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>📍 {inspection.location_description}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<PictureAsPdfIcon />} variant="contained" onClick={() => downloadReport('pdf')} disabled={generating}
            color="primary">PDF</Button>
          <Button startIcon={<TableChartIcon />} variant="outlined" onClick={() => downloadReport('csv')} disabled={generating}
          >CSV</Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[['Total', stats.total, theme.palette.primary.main], ['Critical', stats.critical, theme.palette.error.main], ['High', stats.high, theme.palette.warning.main], ['Confirmed', stats.confirmed, theme.palette.success.main]]
          .map(([label, val, color]) => (
              <Grid item xs={6} md={3} key={label as string}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ py: '12px !important' }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>{label}</Typography>
                  <Typography sx={{ color: color as string, fontSize: 28, fontWeight: 'bold' }}>{val}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Upload for analysis */}
      <Card sx={{ border: `2px dashed ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.04)}`, borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box {...getRootProps()} sx={{ textAlign: 'center', py: 2, cursor: 'pointer' }}>
            <input {...getInputProps()} />
            {analysing ? <CircularProgress sx={{ color: theme.palette.primary.main }} /> : (
              <>
                <UploadFileIcon sx={{ color: theme.palette.primary.main, fontSize: 40, mb: 1 }} />
                <Typography sx={{ color: 'text.secondary' }}>Drop an image or click to upload for AI analysis</Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Latest dropped capture: image with overlay + detection list */}
      {droppedImage && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: 'common.black', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
                <DefectOverlay src={droppedImage} detections={lastDetections} alt="Dropped image" />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <LatestCaptureCard detections={lastDetections} />
          </Grid>
        </Grid>
      )}

      {/* Defects table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography sx={{ color: 'text.primary', fontWeight: 'bold', mb: 2 }}>
            {images.length} capture{images.length === 1 ? '' : 's'} · {defects.length} defect
            {defects.length === 1 ? '' : 's'}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Type', 'Severity', 'Confidence', 'Description', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'text.secondary', borderColor: 'divider', fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.groups.map(g => {
                const clean = g.defects.length === 0;
                // A clean capture has no annotated render, so fall back to the
                // original upload.
                // Key only — the URL is minted when the icon is clicked.
                const groupImageKey = clean
                  ? g.image.s3_key
                  : getDefectImageKey(g.defects[0]);
                return (
                  <React.Fragment key={g.imageId}>
                    <TableRow sx={{ bgcolor: 'background.paper' }}>
                      <TableCell colSpan={6} sx={{ borderColor: 'divider', py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 600 }}>
                            📷 Image {g.index} — {new Date(g.timestamp).toLocaleString()} —{' '}
                            {clean
                              ? 'no defects detected'
                              : `${g.defects.length} defect${g.defects.length === 1 ? '' : 's'}`}
                          </Typography>
                          {groupImageKey && (
                            <Tooltip title={clean ? 'View captured image' : 'View annotated image'}>
                              <IconButton
                                size="small"
                                onClick={() => openImageByKey(
                                  groupImageKey,
                                  clean ? `Image ${g.index}` : `Image ${g.index} — annotated`,
                                )}
                                sx={{ color: 'primary.main' }}>
                                <ImageIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {clean ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ borderColor: 'divider', py: 1.25 }}>
                          <Typography sx={{ color: 'success.main', fontSize: 12.5, fontWeight: 600 }}>
                            ✓ No defects detected in this capture
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : g.defects.map(renderDefectRow)}
                  </React.Fragment>
                );
              })}
              {grouped.ungrouped.length > 0 && (
                <React.Fragment key="ungrouped">
                  <TableRow sx={{ bgcolor: 'background.paper' }}>
                    <TableCell colSpan={6} sx={{ borderColor: 'divider', py: 1 }}>
                      <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 600 }}>
                        📷 Ungrouped — {grouped.ungrouped.length} defect{grouped.ungrouped.length === 1 ? '' : 's'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {grouped.ungrouped.map(renderDefectRow)}
                </React.Fragment>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={Boolean(imageDialogUrl)} onClose={closeImageDialog} maxWidth="md" fullWidth>
        <DialogTitle>{imageDialogTitle}</DialogTitle>
        <DialogContent sx={{ px: 0, pt: 0, pb: 2, textAlign: 'center' }}>
          {imageDialogUrl && (
            <Box component="img" src={imageDialogUrl} alt={imageDialogTitle}
              sx={{ width: '100%', maxHeight: 600, objectFit: 'contain' }} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
